import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { injectDispatch } from '@ngrx/signals/events';
import { ClickAction } from '../enums/click-action';
import { UserTypeEnum } from '../enums/auth';
import { PENDING_DEEP_LINK_KEY } from '../constants/push-storage';
import {
  normalizeDeepLinkPayload,
  parseDeepLinkRaw,
  safeValidateDeepLinkPayload,
} from '../helpers/push-payload';
import {
  AssignedVehicleParams,
  JobParams,
  LoadParams,
  NewShipperSignupParams,
  PaymentParams,
  QuoteParams,
} from '../models/push-notification';
import { notificationEvents } from '../+state/notification/events';
import { AuthStore } from '../+state/auth/auth.store';

@Injectable({ providedIn: 'root' })
export class NotificationNavigationService {
  private router = inject(Router);
  private injector = inject(Injector);
  private notificationDispatch = injectDispatch(notificationEvents);

  /** Lazy: avoids AuthStore ↔ FirebaseMessaging ↔ this service cycle at bootstrap. */
  private authStore(): InstanceType<typeof AuthStore> {
    return this.injector.get(AuthStore);
  }

  /**
   * Booking area prefix: `/booking` on freight app, `` on admin app (routes at root).
   */
  private bookingPrefix(): string {
    return this.router.url.startsWith('/booking') ? '/booking' : '';
  }

  private path(segment: string): string {
    const base = this.bookingPrefix();
    const normalized = segment.startsWith('/') ? segment : `/${segment}`;
    return `${base}${normalized}`;
  }

  /** Queue for cold start before Angular router is ready. */
  stashPendingDeepLink(raw: unknown): void {
    try {
      sessionStorage.setItem(PENDING_DEEP_LINK_KEY, JSON.stringify(raw));
    } catch {
      /* ignore quota errors */
    }
  }

  consumePendingDeepLink(): void {
    try {
      const raw = sessionStorage.getItem(PENDING_DEEP_LINK_KEY);
      if (!raw) {
        return;
      }
      sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
      const parsed = parseDeepLinkRaw(raw);
      if (parsed) {
        void this.navigateWithDeepLinkData(parsed);
      }
    } catch {
      sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
    }
  }

  async navigateWithDeepLinkData(raw: unknown): Promise<void> {
    const normalized = normalizeDeepLinkPayload(raw);
    if (!normalized) {
      await this.fallback();
      return;
    }

    const result = safeValidateDeepLinkPayload(
      normalized.click_action,
      normalized.params,
    );
    if (!result.success) {
      console.warn('[deep-link] Invalid payload', {
        action: normalized.click_action,
        issues: result.error.flatten(),
      });
      await this.fallback();
      return;
    }

    const action = normalized.click_action;
    const data = result.data;

    switch (action) {
      case ClickAction.ADMIN_NEW_SHIPPER_SIGNUP_NOTIFICATION_TARGET:
        await this.navigateToShipperDetail(data as NewShipperSignupParams);
        break;

      case ClickAction.ADMIN_LOAD_CLEARANCE_NOTIFICATION_TARGET:
      case ClickAction.OUTBID_NOTIFICATION_TARGET:
      case ClickAction.CARRIER_COUNTER_OFFER_NOTIFICATION_TARGET:
      case ClickAction.SHIPPER_COUNTER_OFFER_NOTIFICATION_TARGET:
      case ClickAction.OFFER_ACCEPTED_CARRIER_NOTIFICATION_TARGET:
      case ClickAction.OFFER_ACCEPTED_SHIPPER_NOTIFICATION_TARGET:
      case ClickAction.ADMIN_BID_APPROVAL_NOTIFICATION_TARGET:
      case ClickAction.ADMIN_BID_APPROVAL_SHIPPER_NOTIFICATION_TARGET:
      case ClickAction.ADMIN_REJECTED_ACCEPTED_LOAD_NOTIFICATION_TARGET:
      case ClickAction.PICKUP_DATE_APPROACHING_NOTIFICATION_TARGET:
      case ClickAction.PAYMENT_RECEIVED_APPROVAL_NOTIFICATION_TARGET:
      case ClickAction.QUOTES_NOTIFICATION_TARGET:
        await this.navigateToQuote(data as QuoteParams);
        break;

      case ClickAction.ADMIN_JOB_APPROVAL_NOTIFICATION_TARGET:
      case ClickAction.NEW_JOB_NOTIFICATION_TARGET:
      case ClickAction.BID_LOAD_MODIFIED_NOTIFICATION_TARGET:
      case ClickAction.SHIPPER_JOB_APPROVED_NOTIFICATION_TARGET:
        await this.navigateToJob(data as JobParams);
        break;

      case ClickAction.JOB_EXPIRED_NOTIFICATION_TARGET:
        await this.navigateToExpiredJob(data as JobParams);
        break;

      case ClickAction.ADMIN_PAYMENT_RECEIVED_NOTIFICATION_TARGET:
      case ClickAction.ADMIN_PAYMENT_DUE_EXPIRING_NOTIFICATION_TARGET:
      case ClickAction.PAYMENT_OVERDUE_NOTIFICATION_TARGET:
        await this.navigateToPayment(data as PaymentParams);
        break;

      case ClickAction.LOAD_PROGRESS_UPDATE_NOTIFICATION_TARGET:
      case ClickAction.LOAD_DELIVERED_NOTIFICATION_TARGET:
      case ClickAction.LOAD_COMPLETED_NOTIFICATION_TARGET:
        await this.navigateToLoad(data as LoadParams);
        break;

      case ClickAction.ADMIN_LOAD_COMPLETION_APPROVAL_TARGET:
        await this.navigateToLoadForAdminApproval(data as LoadParams);
        break;

      case ClickAction.ASSIGNED_VEHICLE_NOTIFICATION_TARGET:
        await this.navigateToTrackLoads(data as AssignedVehicleParams);
        break;

      case ClickAction.TERMS_AND_CONDITIONS_UPDATED_NOTIFICATION_TARGET:
        await this.router.navigateByUrl(this.path('/settings/terms-and-conditions'));
        break;

      case ClickAction.VETTING_SUCCESSFUL_NOTIFICATION_TARGET:
        await this.router.navigateByUrl(this.path('/dashboard'));
        break;

      default:
        await this.fallback();
    }
  }

  private async fallback(): Promise<void> {
    await this.router.navigateByUrl(this.path('/notifications'));
  }

  private isSuperAdmin(): boolean {
    return (
      (this.authStore().userType() as UserTypeEnum | undefined) ===
      UserTypeEnum.SUPER_ADMIN
    );
  }

  private async navigateToJob(data: JobParams): Promise<void> {
    this.notificationDispatch.navigateToJob(data);
    if (this.isSuperAdmin()) {
      await this.router.navigateByUrl(this.path('/jobs/list'));
    } else {
      await this.router.navigateByUrl(this.path('/my-jobs/list'));
    }
  }

  /**
   * To the job list, like other job notifications: the edit page opens only for
   * a job the list selected (fromListOnlyGuard), so a direct edit URL always
   * bounced. The shipper list opens this job in the editor once it has loaded;
   * the admin list highlights its row (the admin editor is not built yet).
   */
  private async navigateToExpiredJob(data: JobParams): Promise<void> {
    this.notificationDispatch.navigateToExpiredJob(data);
    if (this.isSuperAdmin()) {
      await this.router.navigateByUrl(this.path('/jobs/list'));
    } else {
      await this.router.navigateByUrl(this.path('/my-jobs/list'));
    }
  }

  private async navigateToQuote(data: QuoteParams): Promise<void> {
    this.notificationDispatch.navigateToQuote(data);
    if (this.isSuperAdmin()) {
      await this.router.navigateByUrl(this.path('/jobs/list'));
    } else {
      await this.router.navigateByUrl(this.path('/my-jobs/list'));
    }
  }

  private async navigateToLoad(data: LoadParams): Promise<void> {
    this.notificationDispatch.navigateToLoad(data);
    if (this.isSuperAdmin()) {
      await this.router.navigateByUrl(this.path('/jobs/list'));
    } else {
      await this.router.navigateByUrl(this.path('/my-jobs/list'));
    }
  }

  private async navigateToLoadForAdminApproval(
    data: LoadParams,
  ): Promise<void> {
    this.notificationDispatch.navigateToLoad(data);
    await this.router.navigateByUrl(this.path('/jobs/list'));
  }

  private async navigateToPayment(data: PaymentParams): Promise<void> {
    this.notificationDispatch.navigateToPayment(data);
    await this.router.navigateByUrl(this.path('/payments'));
  }

  private async navigateToTrackLoads(data: AssignedVehicleParams): Promise<void> {
    this.notificationDispatch.navigateToTrackLoads(data);
    await this.router.navigateByUrl(this.path('/track-loads'));
  }

  private async navigateToShipperDetail(
    data: NewShipperSignupParams,
  ): Promise<void> {
    this.notificationDispatch.navigateToShipperDetail(data);
    if (this.isSuperAdmin()) {
      await this.router.navigateByUrl(this.path('/shippers'));
    }
  }
}
